import { Core } from "@faire/design-tokens";
import { Upload } from "@faire/web/slate/icons/foundation/Upload";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React, { FC } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import styled from "styled-components";

interface IProps {
  id?: string;
  "data-test-id"?: string;
  acceptedFileTypes?: string[];
  acceptMessage?: string;
  maxSize: number;
  onDropAccepted: (accepted: File[]) => void;
  onDropRejected: (rejected: FileRejection[]) => void;
  dragOver: boolean;
  setDragOver: (dragOver: boolean) => void;
}

export const EmptyDropzone: FC<IProps> = ({
  id,
  acceptedFileTypes,
  maxSize,
  onDropAccepted,
  onDropRejected,
  acceptMessage,
  dragOver,
  setDragOver,
  ...props
}) => {
  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFileTypes,
    multiple: true,
    maxSize,
    onDropAccepted,
    onDropRejected,
    onDragEnter: () => setDragOver(true),
    onDragLeave: () => setDragOver(false),
  });

  const inputProps = {
    id,
    "data-test-id": props["data-test-id"],
  };

  return (
    <HoverableColumn
      $dragOver={dragOver}
      align="center"
      justify="center"
      {...getRootProps()}
    >
      <input {...{ ...getInputProps(), ...inputProps }} role="input" />
      <StyledRow align="center" justify="center">
        {dragOver ? (
          <Typography>
            <StrictLocalMsg
              defaultMessage="Drop here"
              description={{
                text: "Messsage to tell users they can drop files here",
                img: "https://cdn.faire.com/fastly/19040e4db9d4864b014af836f504b4f42d2d608393befca7f501f93f026e9f38.jpeg",
              }}
            />
          </Typography>
        ) : (
          <>
            <Upload color="inherit" fontSize="16px" />
            <Spacer width="1x" />
            <Typography align="center">
              <ResponsiveContentV2
                mobile={
                  <StrictLocalMsg
                    defaultMessage="Upload File(s)"
                    description={{
                      text: "Placeholder text at the center of a file drop box.",
                      img: "https://cdn.faire.com/fastly/c2eee9186be72407cb8031415fec0a26896dfa50e9f203dc3402177163bf40bc.png",
                    }}
                  />
                }
                tabletAndAbove={
                  <StrictLocalMsg
                    defaultMessage="Drag and drop here or <underlineFormatting>upload file(s)</underlineFormatting>"
                    values={{
                      underlineFormatting: UnderlineFormatting,
                    }}
                    description={{
                      text: "Placeholder text at the center of a file drop box.",
                      img: "https://cdn.faire.com/fastly/510890e3213d37d74b60e01ffae27026150d0ca15347aeae1bb24807aa0f6e5c.png",
                    }}
                  />
                }
              />
            </Typography>
          </>
        )}
      </StyledRow>
      {acceptMessage && !dragOver ? (
        <DropzoneLabel align="center" variant="labelSansRegular">
          {acceptMessage}
        </DropzoneLabel>
      ) : null}
    </HoverableColumn>
  );
};

const UnderlineFormatting = (chunk: JSX.Element) => <u>{chunk}</u>;

const DropzoneLabel = styled(Typography)`
  &&& {
    margin: 4px;
    color: ${Core.text.subdued};
    padding: ${getSpacing(0, "2x")};
    max-width: 100%;
  }
`;

const StyledRow = styled(Row)`
  padding: ${getSpacing(0, "2x")};
  max-width: 100%;
`;

const HoverableColumn = styled(Column)<{
  $dragOver?: boolean;
}>`
  outline: 1px dashed ${Core.border.subdued};
  background-color: ${({ $dragOver }) =>
    $dragOver ? Core.surface.info : Core.surface.subdued};
  cursor: pointer;
  width: 100%;
  height: 120px;
  outline-offset: ${getSpacing("-1x")};
  &:hover {
    outline: 1px dashed ${Core.border.default};
  }
`;
